import React, { createContext, useContext, useCallback, useRef, useEffect, useMemo } from 'react';

import { findClosestScrollParentY } from '../helpers/findClosestScrollParentY';

const SelectionContext = createContext(null);

export const NON_QUOTABLE_CLASS_NAME = 'non-quotable';

export function SelectionProvider({ children }) {
    const fieldsRegistry = useRef(new Map());

    const selectionContainsForbiddenElements = useCallback((range) => {
        const forbiddenElements = document.querySelectorAll(`.${NON_QUOTABLE_CLASS_NAME}`);

        if (forbiddenElements.length === 0) return false;

        for (const element of forbiddenElements) {
            const elementRange = document.createRange();
            elementRange.selectNode(element);

            if (range.intersectsNode(element)) {
                return true;
            }

            if (element.contains(range.startContainer) || element.contains(range.endContainer)) {
                return true;
            }

            const comparison =
                range.compareBoundaryPoints(Range.START_TO_START, elementRange) <= 0 &&
                range.compareBoundaryPoints(Range.END_TO_END, elementRange) >= 0;
            if (comparison) {
                return true;
            }
        }

        return false;
    }, []);

    const handleGlobalSelectionChange = useCallback(() => {
        const domSelection = window.getSelection();

        if (
            !domSelection ||
            domSelection.rangeCount === 0 ||
            domSelection.toString().trim() === ''
        ) {
            fieldsRegistry.current.forEach(({ handler }) => {
                handler({
                    text: '',
                    isActive: false,
                    range: null,
                    position: { top: 0, left: 0 },
                });
            });
            return;
        }

        const range = domSelection.getRangeAt(0);
        const selectedText = domSelection.toString().trim();

        const containsForbiddenElements = selectionContainsForbiddenElements(range);

        fieldsRegistry.current.forEach(({ container, handler }) => {
            const isStartInContainer = container.contains(range.startContainer);
            const isEndInContainer = container.contains(range.endContainer);

            if (isStartInContainer && isEndInContainer && !containsForbiddenElements) {
                const rect = range.getBoundingClientRect();

                let scrollParent = findClosestScrollParentY(container);
                const scrollOffset = scrollParent?.scrollTop || window.scrollY || 0;

                handler({
                    text: selectedText,
                    isActive: true,
                    position: {
                        top: rect.top + scrollOffset,
                        left: rect.left + rect.width / 2 + window.scrollX,
                    },
                    range: range.cloneRange(),
                });
            } else {
                handler({
                    text: '',
                    range: null,
                    isActive: false,
                    position: { top: 0, left: 0 },
                });
            }
        });
    }, []);

    const registerField = useCallback((fieldId, containerRef, handler) => {
        const timerId = setTimeout(() => {
            if (containerRef.current) {
                fieldsRegistry.current.set(fieldId, {
                    container: containerRef.current,
                    handler,
                });
            }
        }, 0);

        return () => {
            clearTimeout(timerId);
            fieldsRegistry.current.delete(fieldId);
        };
    }, []);

    useEffect(() => {
        document.addEventListener('selectionchange', handleGlobalSelectionChange);

        return () => {
            document.removeEventListener('selectionchange', handleGlobalSelectionChange);
        };
    }, [handleGlobalSelectionChange]);

    const contextValue = useMemo(() => ({ registerField }), [registerField]);

    return <SelectionContext.Provider value={contextValue}>{children}</SelectionContext.Provider>;
}

export function useSelection() {
    const context = useContext(SelectionContext);
    if (!context) {
        throw new Error('useSelection must be used within a SelectionProvider');
    }
    return context;
}
